/**
 * PATH: apps/quote/src/pages/builder/BuilderCreate.tsx
 * PURPOSE: Auto-create a draft quote and immediately open the builder.
 *
 * WHY:
 * - User clicks "Builder" → wants to be in the editor immediately
 * - No form filling required - just get into the spreadsheet
 * - Quote details can be edited later in the builder header
 *
 * FLOW:
 *   ┌─────────────────────────┐
 *   │ Auto-create draft quote │ (on mount)
 *   └──────────┬──────────────┘
 *              ▼
 *   ┌─────────────────────────┐
 *   │ Redirect /builder/:id   │
 *   └─────────────────────────┘
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { RefreshCw, AlertCircle } from 'lucide-react'
import { quoteApi } from '@/lib/quoteApi'

export default function BuilderCreate() {
  const navigate = useNavigate()
  const hasTriggered = useRef(false)

  const createMutation = useMutation({
    mutationFn: async () =>
      quoteApi.createQuote({
        quote_type: 'quote',
        project_name: `New Quote - ${new Date().toLocaleDateString()}`,
      }),
    onSuccess: (created) => {
      navigate(`/builder/${created.id}`, { replace: true })
    },
  })

  // Auto-create on mount
  useEffect(() => {
    if (!hasTriggered.current) {
      hasTriggered.current = true
      createMutation.mutate()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Error state - show retry button
  if (createMutation.isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Failed to create quote</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {(createMutation.error as Record<string, unknown>)?.response?.data?.detail || 
               (createMutation.error as Record<string, unknown>)?.message || 
               'Please try again'}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {createMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
            Try Again
          </button>
          <button
            onClick={() => navigate('/quotes')}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            View Existing Quotes
          </button>
        </div>
      </div>
    )
  }

  // Loading state
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <RefreshCw className="h-10 w-10 text-blue-600 animate-spin" />
      <div className="text-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Creating new quote...</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Opening builder momentarily</p>
      </div>
    </div>
  )
}
